import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  LocalOfferOutlined as TagIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { buildVisibleTableRows } from "../utils/tableFieldLayout";

const PARCEL_PARTS = [
  {
    label: "Section",
    field_name: "parcel_section",
    field_id: "504c8ac6-6247-43ff-a53e-599fccf08404",
  },
  {
    label: "Township",
    field_name: "parcel_township",
    field_id: "c40587d6-6e3f-4429-8864-80de9206d17d",
  },
  {
    label: "Range",
    field_name: "parcel_range",
    field_id: "015f3afe-c3c2-42b3-a3d9-ba6ffd6de466",
  },
  {
    label: "Subdivision/Plat",
    field_name: "parcel_subdivision",
    field_id: "0b6f1686-568a-4001-8cad-48a90fc3b708",
  },
  {
    label: "Block",
    field_name: "parcel_block",
    field_id: "8414a9f0-225b-443a-b2ec-08f456805cc9",
  },
  {
    label: "Parcel/Lot",
    field_name: "parcel_lot",
    field_id: "e8aabe81-af48-4bc8-b8c3-53f68f90ef95",
  },
];

const keyOf = (field) => field?.field_instance_id || field?.id || field?.name;

const isBoundary = (field) => field?.name?.startsWith("block__");

const resolveTargets = (fields, requested) => {
  const byKey = new Map(fields.map((field) => [keyOf(field), field]));
  return requested.map((field) => byKey.get(keyOf(field)));
};

const getSelection = (tempFields) => {
  const requested = tempFields?.selectedExtraFields?.length
    ? tempFields.selectedExtraFields
    : tempFields?.selectedFormField
      ? [tempFields.selectedFormField]
      : [];

  return [
    ...new Map(
      requested
        .filter((field) => field && !isBoundary(field))
        .map((field) => [keyOf(field), field]),
    ).values(),
  ];
};

const getValidationError = (fields, targets) => {
  if (
    targets.length !== PARCEL_PARTS.length ||
    targets.some((field) => !field || !keyOf(field) || isBoundary(field)) ||
    new Set(targets.map(keyOf)).size !== PARCEL_PARTS.length
  ) {
    return "Select exactly six distinct PDF widgets.";
  }

  const targetKeys = new Set(targets.map(keyOf));
  if (
    fields.filter((field) => targetKeys.has(keyOf(field))).length !==
    PARCEL_PARTS.length
  ) {
    return "The selected widgets could not be uniquely identified. Select them again.";
  }

  if (targets.some((field) => field.type !== "PDFTextField")) {
    return "Parcel parts require six PDF text fields.";
  }

  if (
    targets.some(
      (field) => field.generic_table || field.field_id === "generic_table",
    )
  ) {
    return "Unassign the selected table fields before tagging parcel parts.";
  }

  if (
    targets.some(
      (field) =>
        field.connection || field.text_connection || field.connection_source,
    )
  ) {
    return "Disconnect the selected widgets before tagging parcel parts.";
  }

  const affectedIds = new Set([
    ...targets.map((field) => field.field_id).filter(Boolean),
    ...PARCEL_PARTS.map((part) => part.field_id),
  ]);
  if (
    fields.some(
      (field) =>
        affectedIds.has(field.connection?.source_field_id) ||
        affectedIds.has(field.text_connection?.source_field_id),
    )
  ) {
    return "A selected or parcel field is used by an existing connection. Disconnect it first.";
  }

  return "";
};

const findCatalogField = (catalog, fieldId) => {
  for (const field of Array.isArray(catalog) ? catalog : []) {
    if (field?.field_id === fieldId) return field;
    const child = findCatalogField(field?.children, fieldId);
    if (child) return child;
  }
  return null;
};

const getAssignment = (part, catalog) => {
  const definition = findCatalogField(catalog, part.field_id);
  const label = definition?.field_label || definition?.label || part.label;

  return {
    field_id: part.field_id,
    field_name: part.field_name,
    field_label: label,
    label,
    location: definition?.location || "create",
  };
};

const getSnapshotError = (tempFields, orderedTargets) => {
  const fields = tempFields?.formFields || [];
  const targets = resolveTargets(fields, orderedTargets);
  const validationError = getValidationError(fields, targets);
  if (validationError) return validationError;

  const selectedKeys = new Set(getSelection(tempFields).map(keyOf));
  if (
    selectedKeys.size !== PARCEL_PARTS.length ||
    targets.some(
      (field, index) =>
        !selectedKeys.has(keyOf(field)) || field !== orderedTargets[index],
    )
  ) {
    return "The selection or selected widgets changed. Close this dialog and select the six widgets again.";
  }

  return "";
};

export default function ParcelAutoTagControl({
  currentProcessingFile,
  setCurrentProcessingFile,
  catalog = [],
  pushHistory,
  disabled = false,
  onNotify,
}) {
  const [open, setOpen] = useState(false);
  const [orderedTargets, setOrderedTargets] = useState([]);
  const [error, setError] = useState("");

  const tempFields = currentProcessingFile?.document?.tempFields;
  const fields = tempFields?.formFields || [];
  const requested = getSelection(tempFields);
  const selected = resolveTargets(fields, requested);
  const showControl =
    requested.length === PARCEL_PARTS.length && selected.every(Boolean);
  const validationError = open
    ? getSnapshotError(tempFields, orderedTargets)
    : "";
  const hasExistingTags = orderedTargets.some((field) => field.field_id);

  const close = () => {
    setOpen(false);
    setOrderedTargets([]);
    setError("");
  };

  const start = () => {
    if (disabled || !showControl) return;

    const ordered = buildVisibleTableRows(
      selected,
      tempFields?.currentDocument || {},
    ).flatMap((row) => row.fields);

    setOrderedTargets(ordered);
    setError("");
    setOpen(true);
  };

  const apply = () => {
    if (disabled || typeof setCurrentProcessingFile !== "function") return;

    const snapshotError = getSnapshotError(tempFields, orderedTargets);
    if (snapshotError) {
      setError(snapshotError);
      return;
    }

    const assignments = new Map(
      orderedTargets.map((field, index) => [
        keyOf(field),
        getAssignment(PARCEL_PARTS[index], catalog),
      ]),
    );
    const alreadyAssigned = orderedTargets.every((field) =>
      Object.entries(assignments.get(keyOf(field))).every(
        ([key, value]) => field[key] === value,
      ),
    );

    if (alreadyAssigned) {
      close();
      onNotify?.(
        "success",
        "The selected widgets already have these parcel tags.",
      );
      return;
    }

    pushHistory?.();
    setCurrentProcessingFile((previous) => {
      const previousTemp = previous?.document?.tempFields;
      // Revalidate against the latest state before making an atomic update.
      if (getSnapshotError(previousTemp, orderedTargets)) return previous;

      const nextFields = previousTemp.formFields.map((field) => {
        const assignment = assignments.get(keyOf(field));
        return assignment ? { ...field, ...assignment } : field;
      });
      const byKey = new Map(nextFields.map((field) => [keyOf(field), field]));
      const refresh = (field) =>
        field ? byKey.get(keyOf(field)) || field : field;

      return {
        ...previous,
        document: {
          ...previous.document,
          tempFields: {
            ...previousTemp,
            formFields: nextFields,
            selectedFormField: refresh(previousTemp.selectedFormField),
            selectedExtraFields: (previousTemp.selectedExtraFields || []).map(
              refresh,
            ),
          },
        },
      };
    });

    close();
    onNotify?.("success", "Six widgets tagged with parcel parts successfully.");
  };

  return (
    <>
      {showControl && (
        <Tooltip title="Auto Tag Parcel">
          <span>
            <IconButton
              size="small"
              aria-label="Auto Tag Parcel"
              disabled={
                disabled || typeof setCurrentProcessingFile !== "function"
              }
              onClick={start}
              sx={{
                border: "1px solid #d0d7de",
                borderRadius: 1.5,
                backgroundColor: "transparent",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.08)",
                },
              }}
            >
              <TagIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Dialog
        open={open}
        onClose={close}
        aria-labelledby="parcel-auto-tag-dialog-title"
        maxWidth="xs"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: 2 } }}
      >
        <DialogTitle
          id="parcel-auto-tag-dialog-title"
          sx={{ fontSize: 16, fontWeight: 600, py: 1.5, px: 2 }}
        >
          Auto Tag Parcel
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.5, letterSpacing: 0.8 }}
              >
                Tag Order
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {[
                  "Section",
                  "Township",
                  "Range",
                  "Subdivision",
                  "Block",
                  "Lot",
                ].map((label, i, arr) => (
                  <React.Fragment key={label}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {label}
                    </Typography>
                    {i < arr.length - 1 && (
                      <ArrowForwardIcon
                        sx={{ fontSize: 12, color: "text.disabled" }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                overflow: "hidden",
              }}
            >
              {orderedTargets.map((field, index) => {
                const part = PARCEL_PARTS[index];
                const widgetName =
                  field.name || field.field_instance_id || field.id;
                return (
                  <Box
                    key={keyOf(field)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "20px minmax(0, 1fr) 16px minmax(0, 1fr)",
                      alignItems: "center",
                      columnGap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderTop: index === 0 ? "none" : "1px solid",
                      borderColor: "divider",
                      "&:hover": { backgroundColor: "rgba(25,118,210,0.04)" },
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {index + 1}
                    </Typography>
                    <Tooltip title={widgetName} placement="top-start">
                      <Typography variant="body2" noWrap>
                        {widgetName}
                      </Typography>
                    </Tooltip>
                    <ArrowForwardIcon
                      sx={{ fontSize: 14, color: "text.disabled" }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        sx={{ lineHeight: 1.3 }}
                      >
                        {part.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        component="div"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {part.field_name}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {hasExistingTags && (
              <Alert severity="warning" sx={{ py: 0, fontSize: 13 }}>
                Existing tags on these six widgets will be replaced.
              </Alert>
            )}
            {(error || validationError) && (
              <Alert severity="error" sx={{ py: 0, fontSize: 13 }}>
                {error || validationError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button size="small" onClick={close}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={apply}
            disabled={
              disabled ||
              Boolean(validationError) ||
              typeof setCurrentProcessingFile !== "function"
            }
          >
            Apply Parcel Tags
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
